import { Box, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useApp } from "../../app/context/useApp";
import { useAuth } from "../../app/context/AuthContext";
import ChatHistory from "./components/ChatHistory";
import QueryInput from "./components/QueryInput";
import ModeButtons from "./components/ModeButtons";
import NoKnowledgeBankHint from "./components/NoKnowledgeBankHint";
import KpiSection from "./components/KpiSection";
import AppAlert from "../../ui/components/AppAlert";
import {
  DESKTOP_CHAT_PADDING_X,
  DESKTOP_MODE_STRIP_PADDING_X,
  composerBottomPadSx,
  midDesktopSidebarClearanceSx,
  mobileNavMediaQuery,
  narrowChatSurfaceSx,
} from "../../layout/layoutMetrics";
import { useSidebar } from "../../layout/SidebarContext";

export default function HomePage() {
  const location = useLocation();
  const {
    messages,
    selectedClient,
    alert,
    setAlert,
    activeMode,
  } = useApp();
  const { userInfo } = useAuth();
  const { isSidebarOpen } = useSidebar();
  const midSidebarClearance = midDesktopSidebarClearanceSx(isSidebarOpen);

  const isHomeRoute = location.pathname === "/";
  const showEmptyState =
    messages.length === 0 && (!activeMode || isHomeRoute);
  const showChat = messages.length > 0 && !activeMode;
  const givenName = userInfo?.given_name?.trim();
  const greeting = givenName
    ? `Hi ${givenName}, What’s on your mind?`
    : "What’s on your mind?";

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        position: "relative",
        display: "flex",
      }}
    >
      {/* Centered amber wash + subtle grain — glass picks this up via backdrop-filter */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "40%",
            transform: "translate(-50%, -50%)",
            width: "min(132vmin, 960px)",
            height: "min(132vmin, 960px)",
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 48%, rgba(148, 132, 96, 0.26) 0%, rgba(104, 94, 72, 0.14) 26%, rgba(56, 52, 44, 0.06) 45%, transparent 62%)",
            filter: "blur(2px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.04,
            mixBlendMode: "soft-light",
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
            backgroundSize: "180px 180px",
          }}
        />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {showEmptyState && (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              color: "white",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "safe center",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: 32,
                  fontWeight: 500,
                  flexShrink: 0,
                  [`@media ${mobileNavMediaQuery}`]: {
                    fontSize: 24,
                    px: 2,
                    wordBreak: "break-word",
                  },
                }}
              >
                {greeting}
              </Typography>
              <Box
                sx={{
                  width: { xs: "min(92vw, 100%)", sm: "min(92vw, 50vw)" },
                  maxWidth: 560,
                  mx: "auto",
                  px: { xs: 1, sm: 0 },
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 1.5, sm: 2 },
                  minWidth: 0,
                  flexShrink: 0,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <QueryInput layout="hero" />
                <ModeButtons />
              </Box>
            </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                flexDirection: "column",
                alignItems: "center",
                  flexShrink: 0,
                pb: selectedClient ? 2 : 0,
                [`@media ${mobileNavMediaQuery}`]: selectedClient
                  ? composerBottomPadSx
                  : undefined,
                }}
              >
            <NoKnowledgeBankHint />
              {selectedClient && <KpiSection />}
              </Box>
          </Box>
        )}

        {showChat && (
          <>
            <Box
              sx={{
                mt: 0.5,
                px: DESKTOP_MODE_STRIP_PADDING_X,
                flexShrink: 0,
                [`@media ${mobileNavMediaQuery}`]: { px: 1.5 },
                ...midSidebarClearance,
              }}
            >
              <ModeButtons />
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                px: DESKTOP_MODE_STRIP_PADDING_X,
                [`@media ${mobileNavMediaQuery}`]: {
                  minHeight: 0,
                  overflowX: "hidden",
                  px: 1.5,
                },
              }}
            >
              <ChatHistory />
            </Box>

            <Box
              sx={{
                px: { xs: 4, md: 20, lg: DESKTOP_CHAT_PADDING_X },
                pb: 2,
                pt: 2,
                flexShrink: 0,
                [`@media ${mobileNavMediaQuery}`]: {
                  ...narrowChatSurfaceSx,
                  ...composerBottomPadSx,
                  minWidth: 0,
                  width: "100%",
                  boxSizing: "border-box",
                },
                ...midSidebarClearance,
              }}
            >
              <QueryInput pinToolbar />
            </Box>
          </>
        )}
      </Box>

      <AppAlert
        open={alert?.open || false}
        title={alert?.title || ""}
        message={alert?.message || ""}
        onClose={() => setAlert && setAlert({ open: false, title: "", message: "" })}
      />
    </Box>
  );
}